import { Menu } from '@base-ui/react/menu'
import { Fragment, type ReactElement } from 'react'

import { Icon } from '@fv/client-components'

type AdminMenuItem = {
  label: string
  key: string
  onClick: () => void
  disabled?: boolean
}

type AdminMenuItemGroup = {
  label?: string
  key: string
  items: AdminMenuItem[]
}

function isGroup(
  item: AdminMenuItem | AdminMenuItemGroup,
): item is AdminMenuItemGroup {
  return 'items' in item
}

type AdminPopupMenuProps = {
  triggerClassName?: string
  trigger: ReactElement
  items: (AdminMenuItem | AdminMenuItemGroup)[]
  value?: string
}

export type { AdminMenuItem, AdminMenuItemGroup, AdminPopupMenuProps }

export const AdminPopupMenu = ({
  triggerClassName,
  trigger,
  items,
  value,
}: AdminPopupMenuProps) => {
  const menuItemClassName =
    'px-4 py-2 text-sm cursor-pointer data-[highlighted]:bg-base-200'

  return (
    <Menu.Root>
      <Menu.Trigger className={triggerClassName} render={trigger} />
      <Menu.Portal>
        <Menu.Positioner sideOffset={8} className="z-[999]">
          <Menu.Popup className="bg-base-300 border border-base-300 rounded-box shadow-lg py-1 min-w-40 relative">
            <Menu.RadioGroup value={value}>
              {items.map((entry, i) =>
                isGroup(entry) ? (
                  <Fragment key={entry.key}>
                    {i > 0 && (
                      <Menu.Separator className="border-t border-base-300 my-1" />
                    )}
                    <Menu.RadioGroup>
                      {entry.label && (
                        <Menu.GroupLabel className="px-4 py-1 text-xs font-semibold text-base-content/60">
                          {entry.label}
                        </Menu.GroupLabel>
                      )}
                      {entry.items.map(item => (
                        <Menu.RadioItem
                          value={item.key}
                          key={item.key}
                          className={menuItemClassName}
                          onClick={item.onClick}
                          disabled={item.disabled}
                          closeOnClick
                        >
                          <MenuItemIndicator />
                          {item.label}
                        </Menu.RadioItem>
                      ))}
                    </Menu.RadioGroup>
                  </Fragment>
                ) : (
                  <Menu.RadioItem
                    value={entry.key}
                    key={entry.key}
                    className={menuItemClassName}
                    onClick={entry.onClick}
                    disabled={entry.disabled}
                    closeOnClick
                  >
                    <MenuItemIndicator />
                    {entry.label}
                  </Menu.RadioItem>
                ),
              )}
            </Menu.RadioGroup>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}

const MenuItemIndicator = () => {
  return (
    <Menu.RadioItemIndicator>
      <Icon icon="check" className="-ml-4 pl-2" />
    </Menu.RadioItemIndicator>
  )
}
